import { captureException } from '@sentry/browser'
import { useEffect, useMemo } from 'react'
import {
  isRouteErrorResponse,
  useRouteError,
  useSearchParams,
} from 'react-router'
import { RedirectLoopError } from 'src/utils/error'
import { reportedErrors } from 'src/utils/reported-errors'
import { isExpectedRouteError, toReportableError } from 'src/utils/route-error'
import AppErrorState from './AppErrorState'

function parseErrorFromSearchParam(params: URLSearchParams) {
  const code = params.get('code')

  if (code === 'redirect-loop') {
    const pathname = params.get('pathname')
    if (!pathname) return null
    return new RedirectLoopError(pathname)
  }

  return null
}

/**
 * Parse the error from search params and report it to Sentry.
 * Then remove the report flag `?report=1` from the search params.
 */
function useErrorFromSearchParams() {
  const [params, setParams] = useSearchParams()

  const error = useMemo(() => parseErrorFromSearchParam(params), [params])

  const shouldReport = params.get('report') === '1'

  useEffect(() => {
    if (!shouldReport || !error) return

    captureException(error)
    console.error(error)

    setParams(params => {
      params.delete('report')
      return params
    })
  }, [shouldReport, error, setParams])

  return error
}

export default function ErrorPage() {
  const routeError = useRouteError()

  // The route error is unpersisted, so it's safe to report it once the error changed.
  useEffect(() => {
    if (!routeError) return
    // onCaughtError (in entry.client.tsx) fires synchronously during React's
    // commit phase — before this useEffect — and marks render errors in
    // reportedErrors. Loader/action errors are never marked there because
    // React Router handles them via data flow, not Error Boundary catch.
    // onCaughtError already called console.error for this error.
    if (routeError instanceof Object && reportedErrors.has(routeError)) return
    // 4xx route responses (404, 403, …) are user-facing, not bugs.
    if (isExpectedRouteError(routeError)) {
      console.info('Route error (not reported):', routeError)
      return
    }
    captureException(toReportableError(routeError))
    console.error(routeError)
  }, [routeError])

  // The search params error is persisted in the URL,
  // it can't be reported multiple times.
  const searchParamsError = useErrorFromSearchParams()

  // Possible order: trigger search params error -> REDIRECT -> trigger route error.
  // So we should show the route error first.
  const error = routeError || searchParamsError

  const errorDetail = useMemo((): string => {
    if (!error) return 'No error detail'
    if (isRouteErrorResponse(error)) return String(error.data)
    if (typeof error === 'object' && error !== null && 'message' in error)
      return String((error as { message: unknown }).message)
    return String(error)
  }, [error])

  return <AppErrorState errorDetail={errorDetail} fullscreen />
}
